/**
 * Utility for recursively masking sensitive keys in objects before rendering
 */

const DEFAULT_MASK = '***';

const DEFAULT_SENSITIVE_KEYS = [
  'password',
  'token',
  'authorization',
  'apikey',
  'api_key',
  'secret',
  'bearer',
  'accesstoken',
  'access_token',
  'refreshtoken',
  'refresh_token',
  'privatekey',
  'private_key',
];

export interface RedactOptions {
  /**
   * Keys to mask (case-insensitive). Defaults to common sensitive keys.
   */
  sensitiveKeys?: string[];
  /**
   * Replacement value for masked keys
   */
  mask?: string;
  /**
   * If true, preserve the type information (e.g., mask username but keep structure for auth objects)
   */
  preserveStructure?: boolean;
}

/**
 * Check if value is a plain object (not array, not null)
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Recursively redact sensitive keys from an object or array
 */
export function redactSecrets<T>(input: T, options: RedactOptions = {}): T {
  const {
    sensitiveKeys = DEFAULT_SENSITIVE_KEYS,
    mask = DEFAULT_MASK,
    preserveStructure = true,
  } = options;

  // Create lowercase set for fast lookup
  const sensitiveSet = new Set(sensitiveKeys.map((k) => k.toLowerCase()));

  const redact = (value: unknown): unknown => {
    // Handle arrays
    if (Array.isArray(value)) {
      return value.map(redact);
    }

    // Handle objects
    if (isPlainObject(value)) {
      const result: Record<string, unknown> = {};

      for (const [key, val] of Object.entries(value)) {
        const keyLower = key.toLowerCase();

        // Check if this key should be masked
        if (sensitiveSet.has(keyLower)) {
          // If preserving structure and value is an object, recurse but still mask primitives
          if (preserveStructure && isPlainObject(val)) {
            result[key] = redact(val);
          } else {
            result[key] = val == null ? val : mask;
          }
        } else {
          // Not sensitive - recurse normally
          result[key] = redact(val);
        }
      }

      return result;
    }

    // Primitives pass through
    return value;
  };

  return redact(input) as T;
}
